<template>
    <div>
        <h1 class="tw-text-3xl">
            Neue Reisekostenabrechnung
        </h1>

        <hr class="tw-my-4">

        <div class="tw-bg-danger-secondary tw-rounded-lg tw-p-4 tw-text-danger-primary tw-mb-4"
             v-if="!! errors && errors.length > 0">
            <strong>
                Validierungsfehler
            </strong>

            <ul class="tw-mt-2">
                <li v-for="error in errors">
                    {{ error }}
                </li>
            </ul>
        </div>

        <div v-show="step === 1" class="md:tw-w-1/2">
            <div class="tw-text-lg tw-mb-4 tw-font-bold">
                1/9 Reiseroute
            </div>

            <div class="tw-mb-4 tw-flex tw-flex-wrap tw-w-1/3 md:tw-w-full"
                 v-show="canCreateTravelExpenseReportForCeo && ! travel_expense_report.id">
                <div class="tw-flex tw-items-center tw-py-1">
                    <check-box
                            id="create_for_ceo"
                            :initial-value="true"
                            name="travel_type"
                            v-model="travel_expense_report.create_for_ceo"
                    ></check-box>
                    <label class="tw-ml-1" :for="'create_for_ceo'">
                        Reisekostenabrechnung für CEO erstellen
                    </label>
                </div>
            </div>

            <bordered-input type="textarea">
                <template #label>
                    <label for="route">
                        Wohin bist du gereist?
                    </label>
                </template>
                <template #input>
                    <textarea name="route"
                              id="route"
                              placeholder="Ziel + Zwischenstopps

Bsp.: Du fährst von der Firma nach Berlin und zurück.
Route: Kabelsketal-Berlin-Kabelsketal. Keine Rasthöfe aufzählen! "
                              rows="6"
                              v-model="travel_expense_report.route"
                    ></textarea>
                </template>
            </bordered-input>
        </div>

        <div v-show="step === 2" class="md:tw-w-1/2">
            <div class="tw-text-lg tw-mb-4 tw-font-bold">
                2/9 Anlass der Reise
            </div>

            <bordered-input type="textarea">
                <template #label>
                    <label for="reason_for_trip">
                        Aus welchem Grund bist du verreist?
                    </label>
                </template>
                <template #input>
                    <textarea name="reason_for_trip"
                              id="reason_for_trip"
                              placeholder="Grund der Geschäftsreise: Meeting mit Kunden, Akquise, ..."
                              rows="6"
                              v-model="travel_expense_report.reason_for_trip"
                    ></textarea>
                </template>
            </bordered-input>
        </div>

        <div v-show="step === 3" class="md:tw-w-4/5">
            <div class="tw-text-lg tw-mb-4 tw-font-bold">
                3/9 Dauer der Reise
            </div>

            <div class="tw-flex tw-items-center">
                Wie lange bist du verreist? Bei mehrtägigen Reisen bitte an den Mitteltagen das Häkchen 'Ganzer Tag'
                setzen.
            </div>

            <div class="tw-mt-4">
                <table id="period" class="cu-table">
                    <tr>
                        <th></th>
                        <th>Datum</th>
                        <th>von</th>
                        <th>bis</th>
                        <th>Ganzer Tag</th>
                        <th>Stunden</th>
                        <th></th>
                    </tr>

                    <tr class="period_row" v-for="(day, i) in travel_expense_report.travel_information.period">
                        <td>
                            <p class="day">Tag {{ i + 1 }}</p>
                        </td>
                        <td>
                            <div class="cu-input-simple">
                                <input type="date"
                                       class="period_day"
                                       :id="'period_day' + i"
                                       v-model="day.date"
                                       placeholder="Datum"
                                >
                            </div>
                        </td>
                        <td>
                            <div class="cu-input-simple">
                                <input type="time"
                                       class="period_from"
                                       :id="'period_from' + i"
                                       v-model="day.from"
                                       placeholder="von"
                                       :disabled="day.entire_day"
                                >
                            </div>
                        </td>
                        <td>
                            <div class="cu-input-simple">
                                <input type="time"
                                       class="period_to"
                                       :id="'period_to' + i"
                                       v-model="day.to"
                                       placeholder="bis"
                                       :disabled="day.entire_day"
                                >
                            </div>
                        </td>
                        <td>
                            <button v-if="travel_expense_report.travel_information.period.length >= 3 && i > 0 && i < travel_expense_report.travel_information.period.length - 1"
                                    @click="setEntireDay(day)"
                                    type="button"
                                    class="entire_day"
                                    :class="day.entire_day ? 'cu-btn' : 'cu-btn-default'">
                                <span class="fa fa-check" aria-hidden="true"></span>
                            </button>

                            <input type="hidden" :id="'entire_day' + i" :value="day.entire_day">
                        </td>
                        <td>
                            <p class="period_hours tw-font-bold" v-text="Helpers.getHoursForDayFormatted(day)"></p>
                            <input type="hidden" class="period_hours_data" id="period_hours_data1" name="period_hours[]"
                                   value="">
                        </td>
                        <td>
                            <button v-show="i > 0" @click="deleteDay(day)" type="button" class="cu-btn-default" title="Löschen">
                                <i class="fa fa-trash-o"></i>
                            </button>
                        </td>
                    </tr>
                </table>

                <button type="button" class="cu-btn-default tw-mt-2" id="period_button" @click="addDay">
                    <i class="fa fa-plus"></i> weiteren Tag hinzufügen
                </button>
            </div>
        </div>

        <div v-show="step === 4" class="md:tw-w-4/5">
            <div class="tw-text-lg tw-font-bold">
                4/9 Wie bist du verreist?
            </div>

            <div class="tw-mt-2 tw-flex tw-flex-wrap tw-w-1/2">
                <div class="tw-flex tw-items-center tw-w-1/2 tw-pr-4 tw-py-1" v-for="travelType in TravelTypes">
                    <radio
                            :id="'travel_type_' + travelType.value"
                            :initial-value="travelType.value"
                            name="travel_type"
                            v-model="travel_expense_report.travel_information.transportation_type"
                    ></radio>
                    <label class="tw-ml-1" :for="'travel_type_' + travelType.value" v-text="travelType.title"></label>
                </div>
            </div>

            <div class="tw-mt-8"
                 v-show="travel_expense_report.travel_information.transportation_type === Constants.TRANSPORTATION_TYPE_COMPANY_CAR">
                <div class="tw-font-bold tw-mb-4">
                    Firmenwagen
                </div>

                <div class="">
                    <bordered-input>
                        <template #label>
                            <label for="company_car" class="tw-font-bold">
                                Fahrzeug
                            </label>
                        </template>
                        <template #input>
                            <select name="company_car" id="company_car"
                                    v-model="travel_expense_report.travel_information.company_car.company_car_id">
                                <option v-for="companyCar in companyCars" :id="companyCar.id" :value="companyCar.id">
                                    {{ companyCar.license_plate }} ({{ companyCar.brand }})
                                </option>
                            </select>
                        </template>
                    </bordered-input>
                </div>

                <table id="company_car_table" class="cu-table cu-table-no-padding-x">
                    <tr>
                        <th>Tankkosten</th>
                        <th>sonstige Kosten</th>
                        <th>Beschreibung sonstige Kosten</th>
                        <th></th>
                    </tr>

                    <tr class="company_car_row"
                        v-for="cost in travel_expense_report.travel_information.company_car.costs">
                        <td>
                            <div class="cu-input-simple">
                                <input type="number"
                                       class="company_car_tank_cost company_car_cost no-spin-box"
                                       v-model="cost.fuel_costs"
                                >
                            </div>
                        </td>
                        <td class="tw-pl-2">
                            <div class="cu-input-simple">
                                <input type="number"
                                       step="any"
                                       class="company_car_costs no-spin-box"
                                       v-model="cost.other_costs"
                                >
                            </div>
                        </td>
                        <td class="tw-pl-2">
                            <div class="cu-input-simple">
                                <input type="text"
                                       class="company_car_description"
                                       name="company_car_describe_costs[]"
                                       v-model="cost.description_other_costs"
                                >
                            </div>
                        </td>
                        <td>
                            <button v-show="travel_expense_report.travel_information.company_car.costs.length > 1" @click="deleteCompanyCarCost(cost)" type="button" class="cu-btn-default" title="Löschen">
                                <i class="fa fa-trash-o"></i>
                            </button>
                        </td>
                    </tr>

                    <tr>
                        <td>
                            <button type="button" class="cu-btn-default" id="company_car_button"
                                    @click="addCompanyCarCosts">
                                <i class="fa fa-plus"></i>
                                <span>weitere Zeile hinzufügen</span>
                            </button>
                        </td>
                        <td style="text-align: right">
                            Gesamtkosten Firmenwagen:
                        </td>
                        <td>
                            <p class="text-left company_car_total_cost total_cost tw-font-bold"
                               id="company_car_total_cost"
                               v-text="Helpers.companyCarTotalCosts() + ' €'"
                            ></p>
                        </td>
                    </tr>
                </table>
            </div>

            <div class="tw-mt-8"
                 v-show="travel_expense_report.travel_information.transportation_type === Constants.TRANSPORTATION_TYPE_PRIVATE_CAR">
                <div class="tw-font-bold">
                    Privates Kfz
                </div>

                <div class="tw-italic tw-text-blue-grey tw-mb-4">
                    (Bis zum 20. gefahrenen Kilometer werden 0,30€ erstattet, ab dem 21. gefahrenen Kilometer 0,38€. Bundesreisekostengesetz §5 Abs. 2)
                </div>

                <div class="md:tw-w-2/5">
                    <bordered-input>
                        <template #label>
                            <label for="private_car" class="tw-font-bold">
                                Kennzeichen
                            </label>
                        </template>
                        <template #input>
                            <input type="text"
                                   name="private_car"
                                   id="private_car"
                                   placeholder="Kennzeichen eintragen"
                                   v-model="travel_expense_report.travel_information.private_car.licence_plate"
                            >
                        </template>
                    </bordered-input>
                </div>

                <table class="cu-table cu-table-no-padding-x">
                    <tr>
                        <th>km Start</th>
                        <th>km Ende</th>
                        <th>gef. Km</th>
                        <th>Anzahl Mitfahrer</th>
                        <th>Kosten</th>
                        <th></th>
                    </tr>

                    <tr class="private_car_row"
                        v-for="cost in travel_expense_report.travel_information.private_car.costs">
                        <td>
                            <div class="cu-input-simple">
                                <input type="number"
                                       class="private_car_km_start no-spin-box"
                                       id="private_car_km_start0"
                                       v-model="cost.mileage_start"
                                >
                            </div>
                        </td>
                        <td>
                            <div class="cu-input-simple">
                                <input type="number"
                                       class="private_car_km_end no-spin-box"
                                       v-model="cost.mileage_end"
                                >
                            </div>
                        </td>
                        <td>
                            <p class="private_car_gef_km"
                               id="private_car_gef_km0"
                               v-text="Helpers.calculateMileage(cost)"
                            ></p>
                        </td>
                        <td>
                            <div class="cu-input-simple">
                                <input type="number"
                                       class="private_car_passengers"
                                       v-model="cost.number_of_passengers"
                                >
                            </div>
                        </td>
                        <td>
                            <p class="private_car_cost" id="private_car_cost0"
                               v-text="Helpers.privateCarCosts(cost)"></p>
                        </td>
                        <td>
                            <button v-show="travel_expense_report.travel_information.private_car.costs.length > 1" @click="deletePrivateCarCost(cost)" type="button" class="cu-btn-default" title="Löschen">
                                <i class="fa fa-trash-o"></i>
                            </button>
                        </td>
                    </tr>

                    <tr>
                        <td>
                            <button type="button" class="cu-btn-default" id="private_car_button"
                                    @click="addPrivateCarCosts">
                                <i class="fa fa-plus"></i> weitere Zeile hinzufügen
                            </button>
                        </td>
                        <td></td>
                        <td></td>
                        <td class="tw-text-right">
                            Gesamtkosten privates Kfz:
                        </td>
                        <td>
                            <p class="tw-font-bold private_car_total_cost total_cost"
                               id="private_car_total_cost"
                               v-text="Helpers.privateCarTotalCosts() + ' €'"
                            ></p>
                        </td>
                    </tr>
                </table>
            </div>

            <div class="tw-mt-8"
                 v-show="travel_expense_report.travel_information.transportation_type === Constants.TRANSPORTATION_TYPE_PUBLIC_TRANSPORTATION">
                <div class="tw-font-bold">
                    Öffentliche Verkehrsmittel
                </div>

                <table id="public_transportation" class="cu-table cu-table-no-padding-x">
                    <tr>
                        <th>Art</th>
                        <th>Kosten</th>
                        <th></th>
                    </tr>

                    <tr class="public_transportation_row"
                        v-for="cost in travel_expense_report.travel_information.public_transportation.costs">
                        <td>
                            <div class="cu-input-simple">
                                <input type="text"
                                       class="public_transportation_type"
                                       v-model="cost.type"
                                >
                            </div>
                        </td>
                        <td>
                            <div class="cu-input-simple">
                                <input type="number"
                                       class="public_transportation_costs"
                                       name="public_transportation_costs[]"
                                       step=".01"
                                       v-model="cost.amount"
                                >
                            </div>
                        </td>
                        <td>
                            <button v-show="travel_expense_report.travel_information.public_transportation.costs.length > 1" @click="deletePublicTransportationCost(cost)" type="button" class="cu-btn-default" title="Löschen">
                                <i class="fa fa-trash-o"></i>
                            </button>
                        </td>
                    </tr>

                    <tr>
                        <td>
                            <button type="button" class="cu-btn-default" id="public_transportation_button"
                                    @click="addPublicTransportationCost">
                                <span class="fa fa-plus"></span> weitere Zeile hinzufügen
                            </button>
                        </td>
                        <td class="tw-text-right">
                            Gesamtkosten öffentliche Verkehrsmittel:
                            <p class="tw-inline tw-ml-5 tw-font-bold public_transportation_total_cost total_cost"
                               id="public_transportation_total_cost"
                               v-text="Helpers.publicTransportationTotalCosts() + ' €'"
                            ></p>
                        </td>
                    </tr>
                </table>
            </div>
        </div>

        <div v-show="step === 5" class="md:tw-w-2/3">
            <div class="tw-text-lg tw-font-bold">
                5/9 Übernachtung
            </div>

            <div class="tw-font-bold tw-mt-2">
                Wurden die Übernachtungskosten selbst gezahlt?
            </div>

            <div class="tw-mt-2 tw-flex tw-flex-wrap tw-w-1/3">
                <div class="tw-flex tw-items-center tw-w-1/2 tw-pr-4 tw-py-1">
                    <radio
                        id="overnight_self_paid_true"
                        name="overnight_self_paid"
                        :initial-value="true"
                        v-model="travel_expense_report.travel_information.overnight_stay.self_paid"
                    ></radio>
                    <label class="tw-ml-1" :for="'overnight_self_paid_true'">
                        Ja
                    </label>
                </div>

                <div class="tw-flex tw-items-center tw-w-1/2 tw-pr-4 tw-py-1">
                    <radio
                        id="overnight_self_paid_false"
                        name="overnight_self_paid"
                        :initial-value="false"
                        v-model="travel_expense_report.travel_information.overnight_stay.self_paid"
                    ></radio>
                    <label class="tw-ml-1" :for="'overnight_self_paid_false'">
                        Nein
                    </label>
                </div>
            </div>

            <div v-if="travel_expense_report.travel_information.overnight_stay.self_paid === true">
                <div class="tw-py-4">
                    Es sind von der Hotelrechnung <b>ausschließlich</b> die Übernachtungskosten einzutragen,
                    <b>keine</b> Kosten für Frühstück, Abendessen, Getränke, Minibar, Parkplatz etc.
                </div>

                <div class="tw-mt-4">
                    <div class="tw-flex">
                        <div class="tw-w-1/2">
                            <bordered-input>
                                <template #label>
                                    <label for="overnight_stay_place">
                                        Name und Ort des Hotels
                                    </label>
                                </template>
                                <template #input>
                                    <input type="text"
                                           name="overnight_stay_place"
                                           id="overnight_stay_place"
                                           placeholder="Name und Ort des Hotels"
                                           v-model="travel_expense_report.travel_information.overnight_stay.hotel"
                                    >
                                </template>
                            </bordered-input>
                        </div>

                        <div class="tw-w-1/2 tw-pl-4">
                            <bordered-input>
                                <template #label>
                                    <label for="overnight_stay_place">
                                        Zahlungsart
                                    </label>
                                </template>
                                <template #input>
                                    <select id="overnight_stay_payment_type"
                                            name="overnight_stay_payment_type"
                                            v-model="travel_expense_report.travel_information.overnight_stay.payment_method"
                                    >
                                        <option v-for="overnightPaymentType in OvernightPaymentTypes"
                                                :value="overnightPaymentType.value"
                                                v-text="overnightPaymentType.title"
                                        ></option>
                                    </select>
                                </template>
                            </bordered-input>
                        </div>
                    </div>

                    <table id="overnight_stay" class="cu-table cu-table-no-padding-x">
                        <tr>
                            <th>Personen</th>
                            <th>Anzahl der Nächte</th>
                            <th>Kosten/Nacht</th>
                            <th>Kosten</th>
                            <th></th>
                        </tr>

                        <tr class="overnight_stay_row"
                            v-for="cost in travel_expense_report.travel_information.overnight_stay.costs">
                            <td>
                                <div class="cu-input-simple">
                                    <input type="text"
                                           class="overnight_stay_persons"
                                           placeholder=""
                                           v-model="cost.person_name"
                                    >
                                </div>
                            </td>
                            <td>
                                <div class="cu-input-simple">
                                    <input type="number" min="0"
                                           class="overnight_stay_night overnight_stays"
                                           placeholder=""
                                           v-model="cost.number_of_nights"
                                    >
                                </div>
                            </td>
                            <td>
                                <div class="cu-input-simple">
                                    <input type="number" min="0"
                                           class="overnight_stay_cost_night overnight_stays"
                                           placeholder=""
                                           v-model="cost.amount_per_night"
                                    >
                                </div>
                            </td>
                            <td>
                                <p class="overnight_stay_costs"
                                   id="overnight_stay_costs0"
                                   v-text="Helpers.overnightStayCosts(cost) + ' €'"
                                ></p>
                            </td>
                            <td>
                                <button v-show="travel_expense_report.travel_information.overnight_stay.costs.length > 1" @click="deleteOvernightStayCost(cost)" type="button" class="cu-btn-default" title="Löschen">
                                    <i class="fa fa-trash-o"></i>
                                </button>
                            </td>
                        </tr>

                        <tr>
                            <td colspan="2">
                                <button type="button" class="cu-btn-default" id="overnight_stay_button"
                                        @click="addOvernightStayCosts">
                                    <i class="fa fa-plus"></i> weitere Zeile hinzufügen
                                </button>
                            </td>
                            <td class="tw-text-right">
                                Gesamtkosten Übernachtung:
                            </td>
                            <td>
                                <p class="total_cost tw-font-bold"
                                   v-text="Helpers.overnightStayTotalCosts() + ' €'"
                                ></p>
                            </td>
                        </tr>
                    </table>

                    <div class="tw-mb-2 tw-mt-6 tw-font-bold">
                        Beleg (Pflicht)
                    </div>

                    <div class="tw-mt-2 tw-flex">
                        <multi-file-upload
                            id="overnight_attachments"
                            key="overnight_attachments"
                            index="overnight_attachments"
                            block-layout
                            accept-file-types="image/*"
                            :old="oldData"
                            :attachment-type-id="overnightAttachmentTypeId"
                            @update-attachment-ids="updateAttachmentIds($event, overnightAttachmentTypeId)"
                        ></multi-file-upload>
                    </div>

                    <div class="tw-italic tw-mt-2">
                        Hinweis: Es sind nur Bilder erlaubt
                    </div>
                </div>
            </div>
        </div>

        <div v-show="step === 6" class="md:tw-w-2/3">
            <div class="tw-text-lg tw-font-bold">
                6/9 sonstige Kosten
            </div>

            <div class="tw-font-bold tw-mt-2">
                Wurden sonstigen Kosten selbst gezahlt?
            </div>

            <div class="tw-mt-2 tw-flex tw-flex-wrap tw-w-1/3">
                <div class="tw-flex tw-items-center tw-w-1/2 tw-pr-4 tw-py-1">
                    <radio
                        id="other_cost_self_paid_true"
                        name="other_cost_self_paid"
                        :initial-value="true"
                        v-model="travel_expense_report.travel_information.other_cost.self_paid"
                    ></radio>
                    <label class="tw-ml-1" :for="'other_cost_self_paid_true'">
                        Ja
                    </label>
                </div>

                <div class="tw-flex tw-items-center tw-w-1/2 tw-pr-4 tw-py-1">
                    <radio
                        id="other_cost_self_paid_false"
                        name="other_cost_self_paid"
                        :initial-value="false"
                        v-model="travel_expense_report.travel_information.other_cost.self_paid"
                    ></radio>
                    <label class="tw-ml-1" :for="'other_cost_self_paid_false'">
                        Nein
                    </label>
                </div>
            </div>

            <div v-if="travel_expense_report.travel_information.other_cost.self_paid === true">
                <table id="other_cost" class="cu-table cu-table-no-padding-x">
                    <tr>
                        <th>Art</th>
                        <th>Kosten</th>
                        <th></th>
                    </tr>

                    <tr class="other_cost_row" v-for="cost in travel_expense_report.travel_information.other_cost.costs">
                        <td>
                            <div class="cu-input-simple">
                                <input type="text"
                                       class="other_cost_type"
                                       name="other_cost_type[]"
                                       v-model="cost.type"
                                >
                            </div>
                        </td>
                        <td>
                            <div class="cu-input-simple">
                                <input type="number"
                                       class="other_costs"
                                       name="other_costs[]"
                                       step=".01"
                                       v-model="cost.amount"
                                >
                            </div>
                        </td>
                        <td>
                            <button v-show="travel_expense_report.travel_information.other_cost.costs.length > 1" @click="deleteOtherCost(cost)" type="button" class="cu-btn-default" title="Löschen">
                                <i class="fa fa-trash-o"></i>
                            </button>
                        </td>
                    </tr>

                    <tr>
                        <td>
                            <button type="button" class="cu-btn-default" id="other_cost_button" @click="addOtherCosts">
                                <i class="fa fa-plus"></i> weitere Zeile hinzufügen
                            </button>
                        </td>

                        <td class="tw-text-right">
                            <span>
                                Gesamtkosten sonstige:
                            </span>
                            <p class="tw-inline tw-ml-5 other_cost_total total_cost tw-font-bold"
                               v-text="Helpers.otherCostsTotal() + ' €'"
                            ></p>
                        </td>
                    </tr>
                </table>

                <div class="tw-mb-2 tw-mt-6 tw-font-bold">
                    Beleg (Pflicht)
                </div>

                <div class="tw-mt-2 tw-flex">
                    <multi-file-upload
                        id="other_cost_attachments"
                        key="other_cost_attachments"
                        index="other_cost_attachments"
                        block-layout
                        accept-file-types="image/*"
                        :old="oldData"
                        :attachment-type-id="otherCostAttachmentTypeId"
                        @update-attachment-ids="updateAttachmentIds($event, otherCostAttachmentTypeId)"
                    ></multi-file-upload>
                </div>

                <div class="tw-italic tw-mt-2">
                    Hinweis: Es sind nur Bilder erlaubt
                </div>
            </div>
        </div>

        <div v-show="step === 7" class="md:tw-w-2/3">
            <div class="tw-text-lg tw-font-bold">
                7/9 Verpflegungsmehraufwand
            </div>

            <div class="tw-font-bold tw-mt-2">
                Bist du Azubi oder Praktikant?
            </div>

            <div class="tw-mt-2 tw-flex tw-flex-wrap tw-w-1/3">
                <div class="tw-flex tw-items-center tw-w-1/2 tw-pr-4 tw-py-1">
                    <radio
                            id="daily_allowance_1"
                            name="daily_allowance"
                            :initial-value="1"
                            v-model="travel_expense_report.travel_information.daily_allowance"
                    ></radio>
                    <label class="tw-ml-1" :for="'daily_allowance_1'">
                        Ja
                    </label>
                </div>

                <div class="tw-flex tw-items-center tw-w-1/2 tw-pr-4 tw-py-1">
                    <radio
                            id="daily_allowance_0"
                            name="daily_allowance"
                            :initial-value="0"
                            v-model="travel_expense_report.travel_information.daily_allowance"
                    ></radio>
                    <label class="tw-ml-1" :for="'daily_allowance_0'">
                        Nein
                    </label>
                </div>
            </div>

            <div class="tw-text-blue-grey tw-italic"
                 v-show="! travel_expense_report.travel_information.daily_allowance">
                Verpflegungsmehraufwand wird nur Auszubildenden und Praktikanten ausgezahlt. Alle anderen können eine
                Kopie der Reisekostenabrechnung bei Ihrer Einkommenssteuermeldung geltend machen.
            </div>

            <div class="tw-mt-4" v-show="travel_expense_report.travel_information.daily_allowance">
                <table class="cu-table cu-table-no-padding-x">
                    <thead>
                    <tr>
                        <th>Reisedauer</th>
                        <th>pauschaler Tagessatz</th>
                        <th>Anzahl laut o.a. Reisedauer</th>
                        <th>Betrag</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td>Ganzer Tag</td>
                        <td v-text="Helpers.formatPrice(Constants.DAILY_ALLOWANCE_24_HOURS)"></td>
                        <td v-text="Helpers.numberOfDaysWith24Hours()"></td>
                        <td v-text="Helpers.formatPrice(Helpers.numberOfDaysWith24Hours() * Constants.DAILY_ALLOWANCE_24_HOURS)"></td>
                    </tr>
                    <tr>
                        <td>Tage über 8 Std.</td>
                        <td v-text="Helpers.formatPrice(Constants.DAILY_ALLOWANCE_8_TO_24_HOURS)"></td>
                        <td v-text="Helpers.numberOfDaysWith8To24Hours()"></td>
                        <td v-text="Helpers.formatPrice(Helpers.numberOfDaysWith8To24Hours() * Constants.DAILY_ALLOWANCE_8_TO_24_HOURS)"></td>
                    </tr>
                    <tr>
                        <td>Tage bis 8 Std.</td>
                        <td v-text="Helpers.formatPrice(Constants.DAILY_ALLOWANCE_UP_TO_8_HOURS)"></td>
                        <td v-text="Helpers.numberOfDaysWithUpTo8Hours()"></td>
                        <td v-text="Helpers.formatPrice(Helpers.numberOfDaysWithUpTo8Hours() * Constants.DAILY_ALLOWANCE_UP_TO_8_HOURS)"></td>
                    </tr>
                    <tr>
                        <td>An-/Abreisetage</td>
                        <td v-text="Helpers.formatPrice(Constants.DAILY_ALLOWANCE_8_TO_24_HOURS)"></td>
                        <td v-text="Helpers.numberOfArrivalAndDepartureDays()"></td>
                        <td v-text="Helpers.formatPrice(Helpers.numberOfArrivalAndDepartureDays() * Constants.DAILY_ALLOWANCE_8_TO_24_HOURS)"></td>
                    </tr>
                    <tr>
                        <td>Tagesübergreifend</td>
                        <td v-text="Helpers.formatPrice(Constants.DAILY_ALLOWANCE_8_TO_24_HOURS)"></td>
                        <td v-text="Helpers.numberOfCrossDays()"></td>
                        <td v-text="Helpers.formatPrice(Helpers.numberOfCrossDays() * Constants.DAILY_ALLOWANCE_8_TO_24_HOURS)"></td>
                    </tr>
                    <tr>
                        <td></td>
                        <td></td>
                        <td>Gesamtkosten Verpflegungsmehraufwand:</td>
                        <td class="tw-font-bold" v-text="Helpers.formatPrice(Helpers.dailyAllowanceTotalCosts())"></td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div v-show="step === 8" class="md:tw-w-2/3">
            <div class="tw-text-lg tw-font-bold">
                8/9 Kostenrechnung
            </div>

            <div class="tw-flex tw-mt-4">
                <div class="tw-w-1/2">
                    <bordered-input>
                        <template #label>
                            <label for="cost_center">
                                Kostenstelle
                            </label>
                        </template>
                        <template #input>
                            <select name="cost_center" id="cost_center" v-model="travel_expense_report.cost_center_id">
                                <option value="">
                                    Kostenstelle auswählen...
                                </option>

                                <option v-for="costCenter in costCenters" :value="costCenter.id">
                                    {{ costCenter.name }}
                                </option>
                            </select>
                        </template>
                    </bordered-input>
                </div>

                <div class="tw-w-1/2 tw-pl-4">

                    <bordered-input>
                        <template #label>
                            <label for="cost_carrier">
                                Kostenträger
                            </label>
                        </template>
                        <template #input>
                            <cost-carrier-select
                                    disable-branch-nodes
                                    :cost-centers="costCarriers"
                                    name="cost_carrier"
                                    id="cost_carrier"
                                    :value="travel_expense_report.cost_carrier_id"
                                    @change="onCostCarrierChanged"
                            ></cost-carrier-select>
                        </template>
                    </bordered-input>
                </div>
            </div>
        </div>

        <div v-show="step === 9">
            <div class="tw-text-lg tw-font-bold tw-mb-4">
                9/9 Bestätigen & Speichern
            </div>

            <button class="cu-link" @click="showConclusion">
                Zusammenfassung ansehen
            </button>

            <div class="tw-flex tw-items-center tw-py-1 tw-mt-4">
                <check-box
                        id="confirmation"
                        :initial-value="true"
                        name="confirmation"
                        v-model="travel_expense_report.confirmation"
                ></check-box>
                <label class="tw-ml-1" :for="'confirmation'">
                    Ich bestätige die Reisekostenabrechnung und verzichte auf eine Unterschrift.
                </label>
            </div>

            <div class="tw-mt-4">
                <button type="button" class="tw-w-24 cu-btn-default tw-mr-1" @click="previousStep" v-show="step > 1">
                    Zurück
                </button>

                <button class="tw-w-24 cu-btn tw-mr-1" @click="save(true)">
                    Speichern
                </button>

                <button class="tw-w-24 cu-btn-default" @click="save(false)">
                    Entwurf
                </button>
            </div>
        </div>

        <div class="tw-mt-4" v-show="step < 9">
            <button type="button" class="cu-btn-default tw-mr-1" @click="previousStep" v-show="step > 1">
                Zurück
            </button>

            <button type="button" class="cu-btn" @click="nextStep">
                Weiter
            </button>
        </div>
    </div>
</template>

<script>
    import BorderedInput from "../Redesign/BorderedInput";
    import MultiFileUpload from "../Redesign/MultiFileUpload";
    import CheckBox from "../Redesign/CheckBox";
    import Radio from "../Redesign/Radio";
    import CostCarrierSelect from "../CostCarrierSelect";
    import ConclusionModal from "./ConclusionModal";
    import axios from 'axios';

    import {Constants, TravelTypes, OvernightPaymentTypes, Helpers} from "./TravelExpenseReport";

    export default {
        components: {
            BorderedInput,
            MultiFileUpload,
            CheckBox,
            Radio,
            CostCarrierSelect,
            ConclusionModal,
        },

        props: {
            companyCars: {
                type: Array,
                required: true,
            },
            costCenters: {
                type: Array,
                required: true,
            },
            costCarriers: {
                type: Array,
                required: true,
            },
            initialTravelExpenseReport: {
                type: Object,
                required: false,
                default: null,
            },
            canCreateTravelExpenseReportForCeo: {
                required: false,
                type: Boolean,
                default: false,
            },
            redirectUrlAfterSave: {
                type: String,
                required: true,
            },
            oldData: {
                type: Object,
                required: true
            },
            overnightAttachmentTypeId: {
                type: Number,
                required: true
            },
            otherCostAttachmentTypeId: {
                type: Number,
                required: true
            },
        },

        data() {
            let data = {
                Constants: Constants,
                TravelTypes: TravelTypes,
                OvernightPaymentTypes: OvernightPaymentTypes,

                step: 1,
                errors: [],
                isSaving: false,

                travel_expense_report: this.initialTravelExpenseReport !== null ? this.initialTravelExpenseReport : {
                    id: null,
                    user_id: null,
                    passengers: null,
                    reason_for_trip: null,
                    route: null,
                    complete: false,
                    cost_center_id: null,
                    cost_carrier_id: null,
                    confirmation: false,
                    create_for_ceo: false,

                    travel_information: {
                        id: null,
                        period: [
                            {
                                date: null,
                                from: null,
                                to: null,
                                entire_day: false,
                            }
                        ],
                        transportation_type: null,
                        company_car: {
                            company_car_id: null,
                            costs: [
                                {
                                    fuel_costs: null,
                                    other_costs: null,
                                    description_other_costs: null,
                                }
                            ]
                        },
                        private_car: {
                            licence_plate: null,
                            costs: [
                                {
                                    mileage_start: null,
                                    mileage_end: null,
                                    number_of_passengers: 0,
                                }
                            ]
                        },
                        public_transportation: {
                            costs: [
                                {
                                    type: null,
                                    amount: null,
                                }
                            ]
                        },
                        overnight_stay: {
                            self_paid: null,
                            hotel: null,
                            payment_method: null,
                            costs: [
                                {
                                    person_name: null,
                                    number_of_nights: null,
                                    amount_per_night: null,
                                }
                            ],
                            attachment_ids: [],
                        },
                        other_cost: {
                            self_paid: null,
                            costs: [
                                {
                                    type: null,
                                    amount: null,
                                }
                            ],
                            attachment_ids: [],
                        },
                        daily_allowance: null,
                    }
                },
            }

            data.Helpers = new Helpers(data.travel_expense_report);

            return data;
        },

        methods: {
            updateAttachmentIds(attachmentIds, attachmentTypeId) {
                if (attachmentTypeId === this.overnightAttachmentTypeId) {
                    this.travel_expense_report.travel_information.overnight_stay.attachment_ids = attachmentIds ?? [];
                } else if (attachmentTypeId === this.otherCostAttachmentTypeId) {
                    this.travel_expense_report.travel_information.other_cost.attachment_ids = attachmentIds ?? [];
                }
            },

            nextStep() {
                const onSuccess = response => {
                  this.step++;
                };
                const onError = () => {};
                this.validate(onSuccess, onError);
            },

            previousStep() {
                if (this.step > 1) {
                    this.step--;
                }
            },

            validate(onSuccess, onError) {
                this.errors = [];

                axios
                    .post('/travel-expense-report/wizard/validate/' + this.step, this.travel_expense_report)
                    .then(response => {
                        if (typeof onSuccess === 'function') {
                            onSuccess(response);
                        }
                    })
                    .catch(error => {
                        if (!!error.response.data.errors) {
                            this.errors = error.response.data.errors;
                            onError();
                        }
                    })
            },

            showConclusion() {
                this.$modal.show(ConclusionModal, {
                    travelExpenseReport: this.travel_expense_report,
                    costCenters: this.costCenters,
                    costCarriers: this.costCarriers,
                }, {height: 'auto', width: '66%'});
            },

            save(saveAsDraft) {
                if (this.isSaving === true) {
                    return;
                }

                this.isSaving = true;

                let onSuccess = validationResponse => {
                    this.travel_expense_report.complete = saveAsDraft;

                    if (this.travel_expense_report.id > 0) {
                        axios
                            .patch('/travel-expense-report/wizard/' + this.travel_expense_report.id, this.travel_expense_report)
                            .then(response => {
                                window.location.href = this.redirectUrlAfterSave;
                            })
                            .catch(error => {
                                this.isSaving = false;
                            });
                    } else {
                        axios
                            .post('/travel-expense-report/wizard/', this.travel_expense_report)
                            .then(response => {
                                window.location.href = this.redirectUrlAfterSave;
                            })
                            .catch(error => {
                                this.isSaving = false;
                            });
                    }
                };

                let onError = () => {
                  this.isSaving = false;
                };

                try {
                  this.validate(onSuccess, onError);
                } catch (e) {
                  onError();
                }
            },

            setEntireDay(day) {
                if (day.entire_day) {
                    day.entire_day = false;
                } else {
                    day.from = null;
                    day.to = null;
                    day.entire_day = true;
                }
            },

            deleteDay(day) {
                this.travel_expense_report.travel_information.period = this.travel_expense_report.travel_information.period.filter(currentDay => {
                    return day !== currentDay;
                })
            },

            addDay() {
                this.travel_expense_report.travel_information.period.push({
                    date: null,
                    from: null,
                    to: null,
                    entire_day: false,
                });
            },

            addCompanyCarCosts() {
                this.travel_expense_report.travel_information.company_car.costs.push({
                    fuel_costs: null,
                    other_costs: null,
                    description_other_costs: null,
                });
            },

            deleteCompanyCarCost(cost) {
                this.travel_expense_report.travel_information.company_car.costs = this.travel_expense_report.travel_information.company_car.costs.filter(currentCost => {
                    return cost !== currentCost;
                })
            },

            addPrivateCarCosts() {
                this.travel_expense_report.travel_information.private_car.costs.push({
                    mileage_start: null,
                    mileage_end: null,
                    number_of_passengers: null,
                });
            },

            deletePrivateCarCost(cost) {
                this.travel_expense_report.travel_information.private_car.costs = this.travel_expense_report.travel_information.private_car.costs.filter(currentCost => {
                    return cost !== currentCost;
                })
            },

            addPublicTransportationCost() {
                this.travel_expense_report.travel_information.public_transportation.costs.push({
                    type: null,
                    amount: null,
                });
            },

            deletePublicTransportationCost(cost) {
                this.travel_expense_report.travel_information.public_transportation.costs = this.travel_expense_report.travel_information.public_transportation.costs.filter(currentCost => {
                    return cost !== currentCost;
                })
            },

            addOvernightStayCosts() {
                this.travel_expense_report.travel_information.overnight_stay.costs.push({
                    person_name: null,
                    number_of_nights: null,
                    amount_per_night: null,
                });
            },

            deleteOvernightStayCost(cost) {
                this.travel_expense_report.travel_information.overnight_stay.costs = this.travel_expense_report.travel_information.overnight_stay.costs.filter(currentCost => {
                    return cost !== currentCost;
                })
            },

            addOtherCosts() {
                this.travel_expense_report.travel_information.other_cost.costs.push({
                    type: null,
                    amount: null,
                });
            },

            deleteOtherCost(cost) {
                this.travel_expense_report.travel_information.other_cost.costs = this.travel_expense_report.travel_information.other_cost.costs.filter(currentCost => {
                    return cost !== currentCost;
                })
            },

            onCostCarrierChanged(id) {
                this.travel_expense_report.cost_carrier_id = id;
            },
        },
    }
</script>
